'use client';
import {Box} from '@mui/material';
import Container from '@mui/material/Container';
import {styled} from '@mui/material/styles';
import type {ReactNode} from 'react';
import {ListingEmptyState, type ListingEmptyStateProps} from './ListingEmptyState';
import {ListingPagination, type ListingPaginationProps} from './ListingPagination';

export interface ListingGridProps {
  children?: ReactNode;
  busy?: boolean;
  filter?: ReactNode;
  empty?: ListingEmptyStateProps;
  pagination?: ListingPaginationProps;
}

// Stacks the filter bar above the results. The 24px gap matches the Figma news
// listing (filter row → cards grid); without it the filter sits flush against
// the first card row, since Section/Container add no vertical rhythm.
const Layout = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
}));

const Results = styled(Box, {shouldForwardProp: propName => propName !== '$busy'})<{$busy: boolean}>(({theme, $busy}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(5),
  opacity: $busy ? 0.55 : 1,
  pointerEvents: $busy ? 'none' : 'auto',
}));

// Cards are flat by default (see ListingCard) — only the grid context elevates
// them, so carousels stay shadowless while grid listings keep the Figma shadow.
const Grid = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(4),
  [theme.breakpoints.up('sm')]: {gridTemplateColumns: 'repeat(2, 1fr)'},
  [theme.breakpoints.up('md')]: {gridTemplateColumns: 'repeat(3, 1fr)'},
  '& > *': {boxShadow: theme.customShadows.card},
}));

export function ListingGrid({children, busy = false, filter, empty, pagination}: ListingGridProps) {
  return (
    <Layout>
      {filter && <Container>{filter}</Container>}
      <Results $busy={busy} inert={busy || undefined}>
        <Container>{empty ? <ListingEmptyState {...empty} /> : <Grid>{children}</Grid>}</Container>
        {pagination && !empty && (
          <Container>
            <ListingPagination {...pagination} />
          </Container>
        )}
      </Results>
    </Layout>
  );
}

ListingGrid.displayName = 'ListingGrid';
